import { Button } from '@design-system/button';
import { isErrorResult } from '@phx-husky/auth-service';
import { useProfile } from '@phx-husky/use-profile';
import { useTranslations } from '@phx-husky/use-translations';
import classNames from 'classnames';
import { withPrefix } from 'gatsby';
import React, { useEffect, useState } from 'react';

import {
  addEvent,
  getEventsString,
  isProfileWithEvents,
  isSubscribed,
} from './helpers';
import {
  EventSubscriptionProps,
  ExtendedUserAttributes,
  ProfileWithEvents,
} from './models';

const EventSubscriptionButton = ({ eventPageUid }: EventSubscriptionProps) => {
  const { getProfileFields, setUserAttributes, validate } = useProfile();
  const { translate } = useTranslations();

  const [userProfileData, setUserProfileData] =
    useState<ProfileWithEvents | null>(null);
  const [buttonIsDisabled, setButtonIsDisabled] = useState(true);

  const successMarkIcon = (
    <img src={withPrefix('/images/success-mark.svg')} alt="success-mark.svg" />
  );

  useEffect(() => {
    const fetchProfile = async () => {
      const data = await getProfileFields();
      if (!isProfileWithEvents(data)) return;

      setUserProfileData(data);
      setButtonIsDisabled(false);
    };

    fetchProfile();
  }, [getProfileFields]);

  const currUserEventsSubscriptionStr = getEventsString(userProfileData);

  const isAlreadySubscribed = isSubscribed(
    currUserEventsSubscriptionStr,
    eventPageUid
  );

  const subscribeHandler = async () => {
    if (isAlreadySubscribed) return;

    setButtonIsDisabled(true);

    try {
      const freshProfile = await getProfileFields();

      if (!isProfileWithEvents(freshProfile)) return;

      const freshEventsStr = getEventsString(freshProfile);

      const alreadySubscribedNow = isSubscribed(freshEventsStr, eventPageUid);

      if (alreadySubscribedNow) return;

      const newUserEventsSubscriptionStr = addEvent(
        freshEventsStr,
        eventPageUid
      );

      const isValid = await validate({
        events: newUserEventsSubscriptionStr,
      });

      if (isValid) {
        const payload: ExtendedUserAttributes = {
          events: newUserEventsSubscriptionStr,
        };

        const dataUpdateRes = await setUserAttributes(payload);

        if (isErrorResult(dataUpdateRes)) {
          return;
        }

        setUserProfileData({
          ...freshProfile,
          userDetails: {
            ...freshProfile.userDetails,
            events: {
              ...freshProfile.userDetails.events,
              defaultValue: newUserEventsSubscriptionStr,
            },
          },
        });
      }
    } catch (error) {
      // console.error(error);
    } finally {
      setButtonIsDisabled(false);
    }
  };

  const btnStateClass = isAlreadySubscribed
    ? 'event-sign-up-btn---signed-up'
    : 'event-sign-up-btn--default';

  return (
    <Button
      variant="primary"
      className={classNames('event-sign-up-btn', btnStateClass)}
      type="button"
      disabled={buttonIsDisabled || isAlreadySubscribed}
      onClick={subscribeHandler}
    >
      {isAlreadySubscribed ? (
        <span className="event-sign-up-btn__success">
          <span className="event-sign-up-btn__icon">{successMarkIcon}</span>
          {translate('userEventSubscribedLabel') || 'Subscribed'}
        </span>
      ) : (
        translate('userEventSubscritionLabel') || 'Subscribe'
      )}
    </Button>
  );
};

export default EventSubscriptionButton;
